'use client';

import React, { FC } from 'react';
import NextImage from 'next/image';
import Link from 'next/link';

import { MagicSearch } from 'app/(authenticated)/(member)/_components/MagicSearch';
import { AddDealLink } from 'app/(authenticated)/(member)/_components/CreateHelpRequest';
import { Button } from 'components/ui/button';
import { ChevronRightIcon, PlusIcon } from 'components/icons/v2';
import { DiscoveryIcon } from 'components/icons/v2/DiscoveryIcon';
import { MEMBER_ROUTES, OWNER_ROUTES } from 'utils/routes';
import { CurrentUserType } from 'types';
import { useTracking } from 'app/_tracking';
import { usePathname } from 'next/navigation';
import { useAuth } from 'contexts/auth';

interface HeaderProps {
  logo?: string | null;
  onOpenMenu?: () => void;
  currentUser?: CurrentUserType | null;
}

export const Header: FC<HeaderProps> = (props) => {
  const { logo, onOpenMenu, currentUser } = props;
  const { trackEvent } = useTracking();
  const pathname = usePathname();
  const { currentUser: clientCurrentUser } = useAuth();

  if (!currentUser && !clientCurrentUser) {
    return (
      <header className='flex px-8 py-3 border-b border-b-gray-100 justify-between items-center md:px-3 md:py-[10px] w-full'>
        <div className={'flex flex-row min-w-[25%] justify-between items-center'}>
          <Link href='/'>
            <NextImage src={'/logo/kyg-logo-beta.svg'} width={90} height={35} alt='Logo' className='h-[35px] w-auto' />
          </Link>
          <Link
            href={`${OWNER_ROUTES.REGISTER.getPath()}?redirect=${OWNER_ROUTES.START.getPath()}`}
            onClick={() => {
              trackEvent({ 'name': 'header_create_space_clicked' });
            }}
          >
            <Button
              variant={'nakedBrandLink'}
              size={'medium'}
            >
              <PlusIcon />
              Create space
            </Button>
          </Link>
        </div>

        <div className={'min-w-[25%] flex justify-end'}>
          <Link href={`${OWNER_ROUTES.LOGIN.getPath()}?redirect=${pathname}`}>
            <Button
              variant='nakedGrayLink'
              size='medium'
            >
              Login
            </Button>
          </Link>
        </div>
      </header>
    );
  }

  return (
    <header className='flex px-8 py-3 border-b border-b-gray-100 justify-between items-center md:px-3 md:py-[10px] w-full'>

      {/* Mobile menu button */}
      <Button variant='nakedGrayLink' className='hidden md:flex p-0 gap-2' onClick={onOpenMenu}>
        <div className='w-9 h-9 p-1 rounded-full flex items-center justify-center'>
          {logo ? (
            <NextImage src={logo} width={70} height={24} alt='Logo' className='h-auto w-auto rounded-full' />
          ) : (
            <NextImage src={'/logo/default-community-logo.svg'} width={24} height={24} alt='Logo'
                       className='h-auto w-auto' />
          )}
        </div>
        <ChevronRightIcon />
      </Button>

      {/* Desktop menu */}
      <div className={'md:hidden flex flex-row min-w-[25%] justify-between items-center'}>
        <Link href={MEMBER_ROUTES.MEMBERS.getPath()}>
          <NextImage src={'/logo/kyg-logo-beta.svg'} width={90} height={35} alt='Logo' className='h-[35px] w-auto' />
        </Link>
        <Link
          href={MEMBER_ROUTES.DISCOVERY.getPath()}
          onClick={() => {
            trackEvent({ 'name': 'header_discovery_clicked' })
          }}
        >
          <Button
            variant={'nakedBrandLink'}
            size={'medium'}
          >
            <DiscoveryIcon />
            Discovery spaces
          </Button>
        </Link>
      </div>

      {!MEMBER_ROUTES.WIN_WIN.resolve(pathname || '') && <MagicSearch />}

      <div className={'md:hidden min-w-[25%] flex justify-end'}>
        <AddDealLink />
      </div>
    </header>
  );
};
